/**
 * Lead Intake Types
 * 
 * Defines the canonical incoming lead payload and lifecycle status for the intake layer.
 * These types represent external leads before they enter the routing system.
 */

// --- Intake Lifecycle Status ---

/**
 * Lifecycle status for tracking intake processing.
 * This is distinct from the routing lifecycle (LeadStatus in leadStateMachine).
 */
export type IntakeLifecycleStatus =
  | 'RECEIVED'       // Lead received from external source
  | 'VALIDATED'      // Passed validation, ready for routing
  | 'ROUTING_ATTEMPTED' // Sent to orchestrator
  | 'ROUTED'         // Successfully routed to an agent
  | 'RETRY_QUEUED'   // Queued for retry
  | 'STOCKED'        // Moved to stock (retries exhausted)
  | 'UNSOLD'         // Could not be sold
  | 'VALIDATION_FAILED'; // Failed intake validation

// --- Incoming Lead Payload ---

/**
 * The shape of an incoming lead from external sources (webhook, API, etc.)
 */
export interface IncomingLeadPayload {
  // Required fields
  name?: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  email?: string;
  state: string;
  leadType: string;
  
  // Optional source tracking
  source?: string;
  campaign?: string;
  subId?: string;
  
  // Optional contact preferences
  preferredContactTime?: string;
  language?: string;
  
  // Optional qualification data
  age?: number;
  income?: number;
  coverageAmount?: number;
  
  // Optional metadata
  externalId?: string;
  receivedAt?: number;
  
  // Allow additional fields
  [key: string]: unknown;
}

// --- Normalized Lead ---

/**
 * A normalized lead ready for routing.
 * All fields are validated and formatted.
 */
export interface NormalizedLead {
  id: string;
  
  // Contact info
  name: string;
  phone: string;
  email?: string;
  
  // Geographic
  state: string; // Uppercase 2-letter code
  
  // Classification
  leadTypeId: string;
  leadTypeName: string;
  vertical: 'life_insurance' | 'mortgage';
  
  // Value estimation
  estimatedValue: number;
  
  // Source tracking
  source: string;
  campaign?: string;
  subId?: string;
  externalId?: string;
  
  // Timestamps
  receivedAt: number;
  validatedAt: number;

  // Raw payload (for persistence)
  sourcePayload?: IncomingLeadPayload;
}

// --- Intake Result ---

/**
 * Result of processing a lead through intake.
 */
export interface IntakeResult {
  success: boolean;
  leadId?: string;
  
  // Lifecycle tracking
  intakeStatus: IntakeLifecycleStatus;
  
  // Routing outcome (if attempted)
  routingOutcome?: 'ROUTED' | 'UNSOLD' | 'RETRY_QUEUED' | 'STOCKED' | 'VALIDATION_FAILED';
  
  // Agent assignment (if routed)
  assignedAgentId?: string;
  assignedAgentName?: string;
  chargedAmount?: number;
  contractId?: string;
  
  // Retry info (if queued)
  retryCount?: number;
  nextRetryAt?: number;
  
  // Error info (if failed)
  errors?: IntakeValidationError[];
  
  // Timing
  processingDurationMs: number;
}

// --- Validation ---

export interface IntakeValidationError {
  field: string;
  message: string;
  code: string;
}

// --- Lead Type Mapping ---

/**
 * Maps external lead type names to internal lead type configuration.
 */
export interface LeadTypeMapping {
  id: string;
  name: string;
  vertical: 'life_insurance' | 'mortgage';
  baseValue: number;
  aliases: string[]; // Alternative names that map to this type
}

/**
 * Default lead type mappings for the platform.
 */
export const DEFAULT_LEAD_TYPE_MAPPINGS: LeadTypeMapping[] = [
  // Life Insurance
  {
    id: 'life_fex',
    name: 'Final Expense',
    vertical: 'life_insurance',
    baseValue: 45,
    aliases: ['final expense', 'final_expense', 'fex', 'burial insurance', 'funeral insurance', 'fe'],
  },
  {
    id: 'life_iul',
    name: 'Indexed Universal Life',
    vertical: 'life_insurance',
    baseValue: 75,
    aliases: ['iul', 'indexed universal', 'indexed universal life', 'universal life'],
  },
  {
    id: 'life_mp',
    name: 'Mortgage Protection',
    vertical: 'life_insurance',
    baseValue: 50,
    aliases: ['mortgage protection', 'mortgage_protection', 'mp', 'mpi'],
  },
  
  // Mortgage
  {
    id: 'mortgage_purchase',
    name: 'Purchase',
    vertical: 'mortgage',
    baseValue: 80,
    aliases: ['purchase', 'home purchase', 'buy'],
  },
  {
    id: 'mortgage_refi',
    name: 'Refinance',
    vertical: 'mortgage',
    baseValue: 70,
    aliases: [
      'refinance',
      'refi',
      'refinancing',
      'cash out',
      'cashout',
      'rate term',
      'rate & term',
      'refinance cash out',
      'refinance rate & term',
    ],
  },
  {
    id: 'mortgage_heloc',
    name: 'HELOC',
    vertical: 'mortgage',
    baseValue: 60,
    aliases: ['heloc', 'home equity', 'equity line', 'home equity line'],
  },
  {
    id: 'mortgage_reverse',
    name: 'Reverse Mortgage',
    vertical: 'mortgage',
    baseValue: 60,
    aliases: ['reverse', 'reverse mortgage'],
  },
];

/**
 * Find a lead type mapping by name or alias.
 *
 * Matching is lenient: whitespace, underscores, and hyphens are stripped
 * before comparison so "Mortgage Protection", "MortgageProtection", and
 * "mortgage_protection" all resolve to the same mapping. LeadCapture forms
 * often ship PascalCase values (e.g. "MortgageProtection") that would miss
 * stricter matchers.
 */
export function findLeadTypeMapping(leadType: string): LeadTypeMapping | null {
  const canon = (s: string) => s.toLowerCase().replace(/[\s_\-]+/g, '').trim();
  const normalized = canon(leadType);
  if (!normalized) return null;

  for (const mapping of DEFAULT_LEAD_TYPE_MAPPINGS) {
    if (canon(mapping.name) === normalized) return mapping;
    if (canon(mapping.id) === normalized) return mapping;

    for (const alias of mapping.aliases) {
      if (canon(alias) === normalized) return mapping;
    }
  }

  return null;
}
